import { Spinner } from '@/components/Spinner'
import { CopyrightText } from '@/components/copyright-text'
import { Button } from '@/components/ui/button'
import { useQuery } from '@/hooks/useQuery'
import { isContractSignRequiredError, resetUrqlClient } from '@/urql'
import { maybeGraphQLType } from '@/utils/graphql'
import { graphql } from 'gql.tada'
import Head from 'next/head'
import Image from 'next/image'
import React from 'react'
import { toast } from 'react-toastify'
import { useMutation } from 'urql'

export class ContractSignErrorBoundary extends React.Component<
  { children?: React.ReactNode },
  { hasError: boolean }
> {
  constructor(props: {}) {
    super(props)
    this.state = { hasError: false }
  }

  static getDerivedStateFromError(error: unknown) {
    if (isContractSignRequiredError(error)) {
      return { hasError: true }
    }
  }

  componentDidCatch(error: unknown, _: unknown) {
    if (!isContractSignRequiredError(error)) throw error

    resetUrqlClient()
  }

  render() {
    if (this.state.hasError) {
      return (
        <ContractAgreement
          resetError={() => this.setState({ hasError: false })}
        />
      )
    }

    return this.props.children
  }
}

const ContractAgreement_Query = graphql(`
  query ContractAgreement_Query {
    currentUser {
      __typename
      ... on Node {
        id
      }
      ... on BaseUser {
        notSignedContracts {
          id
          title
          content
        }
      }
    }
  }
`)

const SignContractMutation = graphql(`
  mutation SignContractMutation($input: ContractSignInput!) {
    contractSign(input: $input) {
      contracts {
        id
        signed
      }
      errors {
        __typename
      }
    }
  }
`)

interface ContractAgreementProps {
  resetError(): void
}

function ContractAgreement(props: ContractAgreementProps) {
  const [result] = useQuery({
    query: ContractAgreement_Query,
  })
  const notSignedContracts =
    maybeGraphQLType('User', 'SchoolUser')(result.data?.currentUser)
      ?.notSignedContracts ?? []

  const [{ fetching: signing }, sign] = useMutation(SignContractMutation)

  async function handleSignContracts() {
    const { error, data } = await sign({
      input: { id: notSignedContracts?.map((contract) => contract.id) },
    })

    if (error || data?.contractSign?.errors?.length) {
      return toast.error('失敗しました', {
        toastId: 'contract-sign-failed',
        position: 'bottom-right',
      })
    }

    resetUrqlClient()
    props.resetError()
  }

  return (
    <>
      <Head>
        <title>Eula Agreement</title>
      </Head>

      <main
        className="flex h-full flex-col overflow-auto bg-basic-bg"
        tabIndex={0}
      >
        <header className="flex h-[50px] items-center bg-white md:h-[60px] lg:h-[70px]">
          <Image
            src="/gApp_logo.svg"
            width={120}
            height={32}
            className="ml-[8px] w-[120px] md:ml-[18px] md:w-[150px] lg:ml-[24px] lg:w-[196px]"
            alt="logiQlabo"
          />
        </header>

        <div className="mx-auto mt-[54px] w-[calc(100%-40px)] max-w-[940px] rounded-[8px] bg-white pb-[32px] pt-[32px] md:mt-[70px] md:rounded-[10px] md:pb-[50px] md:pt-[60px] lg:mt-[80px] lg:rounded-[12px]">
          <h2 className="text-center text-[18px] font-bold md:text-[24px]">
            利用規約・プライバシーポリシー
          </h2>

          <div
            tabIndex={0}
            className="mx-auto mt-[16px] grid h-[275px] max-w-[640px] gap-[32px] overflow-y-auto rounded-md border border-outline px-[16px] py-[24px] md:mt-[28px] md:h-[306px] md:gap-[50px] md:px-[30px] md:py-[44px]"
          >
            {notSignedContracts?.map((contract) => (
              <div key={contract.id} className="text-[#828488]">
                <h3 className="mb-[20px] text-center text-[17px] md:mb-[16px] md:text-[20px]">
                  {contract.title}
                </h3>

                <div className="policy-section">
                  <div
                    className="text-[12px] leading-normal md:text-[13px]"
                    dangerouslySetInnerHTML={{
                      __html: contract.content,
                    }}
                  />
                </div>
              </div>
            ))}
          </div>

          <p className="mt-[20px] text-center text-[12px] md:mt-[30px]">
            利用規約およびプライバシーポリシーを確認し、同意の上で次へ進んでださい。
          </p>

          <Button
            onClick={handleSignContracts}
            variant="light"
            className="mx-auto mt-5 flex w-[270px] md:mt-[50px]"
          >
            {signing ? <Spinner /> : 'つぎへ'}
          </Button>
        </div>

        <footer className="mt-auto py-3.5">
          <p className="text-center text-[10px] font-medium text-outline md:text-xs">
            <CopyrightText />
          </p>
        </footer>
      </main>
    </>
  )
}
